import {
  Component,
  ChangeDetectorRef,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  signal,
  computed,
  viewChildren,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import clone from 'lodash-es/clone';
import isEmpty from 'lodash-es/isEmpty';

import { Property } from 'app/modules/projects/projects.types';
import { PanelsInterfaceComponent } from '../panels-interface.component';
import { PartialFormField } from 'app/shared/components/form-fields/form-field.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { Account, FieldDef } from 'app/core/models';
import { getAllowedFields } from 'app/shared/functions';
import { RelatedToType } from 'app/shared/enums';
import { Store } from '@ngxs/store';
import { FeatureFlag, NewPermission } from '@core/enums';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { FormFieldComponent } from '@shared/components/form-fields/form-field.component';

@Component({
  selector: 'property-panel-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsFormComponent extends OnDestroyMixin implements PanelsInterfaceComponent, OnInit {
  @Input() property: Property;
  @Input() account: Account;
  @Input() panelDef: PanelDef<any>;
  @Input() data: any;
  @Input() canEditPanelDef: boolean = true;
  @Input() readonly = false;
  @Output() onUpdate: EventEmitter<PartialFormField> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();

  private defaultFieldDefs: FieldDef[] = [];
  titleFieldDef: any;
  panelName: string;

  public editMode: boolean;
  public fields: any[] = [];
  public fieldsCombined: any[] = [];
  propertyData: any = {};
  private _originalFields: any[] = [];

  fieldDefs = [];
  rowActionDisabled = [];

  savingChanges = signal(false);
  changesToSave = signal({});
  hasPendingChanges = computed(() => Object.keys(this.changesToSave()).length > 0);
  propertyMemento: Property;

  fieldsComponents = viewChildren<FormFieldComponent>('fields');

  constructor(
    private readonly _propertiesService: PropertiesService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store,
  ) {
    super();
  }

  private get isRolePanel(): boolean {
    return this.panelDef.type === 'roles';
  }

  ngOnInit() {
    this.cleanPanels();
    this.fields = clone(this.panelDef.meta.fields);
    this.propertyMemento = clone(this.property);
    this._originalFields = clone(this.panelDef.meta.fields);
    this.titleFieldDef = {
      name: 'panel_name',
      label: this.panelDef.title,
      type: 'text',
    };
    this.fieldDefs = getAllowedFields(this.data?.fieldDefs, RelatedToType.Property);
    this.propertyData = { ...this.data, fieldDefs: this.fieldDefs };

    this._propertiesService.property$.pipe(untilComponentDestroyed(this)).subscribe((property: Property) => {
      if (property) {
        this.property = property;
        this.propertyMemento = clone(property);
        this.reloadFields();
      }

      this._changeDetectorRef.markForCheck();
    });

    if (this.isRolePanel) {
      this.store
        .select(
          ObjectsManagerState.getFieldsByObjectName(RelatedToType.Property, {
            addNestedFields: false,
            allowedFieldsOnly: true,
          }),
        )
        .pipe(untilComponentDestroyed(this))
        .subscribe((fields) => {
          if (fields) {
            this.defaultFieldDefs = fields.filter(
              (fItem) => fItem.task_role_id !== null && fItem.task_role_id !== undefined,
            );
            this.fields = this.defaultFieldDefs.map((item) => [item.name]);
          }
        });
    }

    this.store
      .select(
        ObjectsManagerState.getFieldsByObjectName(RelatedToType.Property, {
          addNestedFields: false,
          allowedFieldsOnly: true,
        }),
      )
      .pipe(untilComponentDestroyed(this))
      .subscribe((fields) => {
        if (fields) this.defaultFieldDefs = fields;
      });

    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      this.readonly = !this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_EDIT));
    }
  }

  cleanPanels() {
    const cleanFields = [];
    this.panelDef.meta.fields.forEach((field) => {
      const cleanField = field.filter(function (e) {
        return e;
      });
      if (cleanField.length > 0)
        cleanFields.push(
          field.filter(function (e) {
            return e;
          }),
        );
    });

    this.panelDef.meta.fields = cleanFields;
    this.fields = clone(this.panelDef.meta.fields);
  }

  createFieldList() {
    this.fieldsCombined = [];
    const leftCol = [],
      rightCol = [];
    this.fields.map((field) => {
      leftCol.push(field[0]);
      rightCol.push(field[1]);
    });
    this.fieldsCombined.push(leftCol);
    this.fieldsCombined.push(rightCol);
  }

  dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Toggle edit mode
   *
   * @param editMode
   */
  toggleEditMode(editMode: boolean | null = null): void {
    this.cleanPanels();

    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }

    if (this.editMode === false) {
      this.panelName = null;
      this.property[this.titleFieldDef.name] = null;
    }

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  cancelEdits(): void {
    this.fields = this._originalFields;

    this.panelDef.meta.fields = clone(this._originalFields);

    this.toggleEditMode(false);
  }

  /**
   * Save edits to db
   */
  saveLayout(): void {
    this.panelDef.meta.fields = this.fields.filter((row) => row && row.length > 0);
    if (this.panelName) {
      this.panelDef.title = this.panelName;
      this.titleFieldDef.label = this.panelName;
    }
    this.onEditPanelDef.emit(this.panelDef);
    this.toggleEditMode(false);
  }

  onFieldSelectionChange($event, row, col): void {
    this.fields[row][col] = $event[this.fields[row][col]] || this.fields[row][col];
  }

  /**
   * Remove a field from row & col
   */
  removeField(row, col): void {
    this.fields[row].splice(col, 1);
    if (isEmpty(this.fields[row])) {
      this.fields.splice(row, 1);
    }
    this._changeDetectorRef.markForCheck();
  }

  addField(row: number, lastCol: number): void {
    const filterFields: FieldDef[] = this.propertyData.fieldDefs.filter(
      (value: FieldDef) => !this.fields[row].includes(value.name),
    );

    let fieldToBeAdded = 'id';

    if (filterFields.length > 0) fieldToBeAdded = filterFields[0].name;

    this.fields[row].push(fieldToBeAdded);

    this._changeDetectorRef.markForCheck();
  }

  addRow(index: number): void {
    if (this.fields.length > 0) {
      this.fields.splice(index + 1, 0, ['id']);
      this.panelDef.meta.fields.splice(index + 1, 0, ['id']);
    } else {
      this.fields.push(['id']);
      this.panelDef.meta.fields.push(['id']);
    }
    this._changeDetectorRef.markForCheck();
  }

  update(params) {
    const currentKey = Object.keys(params)[0];
    if (currentKey === this.titleFieldDef.name) {
      this.panelName = params[this.titleFieldDef.name];
    } else {
      const selectColumn = this.defaultFieldDefs.map((m) => m.name) || [];
      selectColumn.push(currentKey);
      this.changesToSave.set({ ...this.changesToSave(), ...params, id: this.property?.id });
    }
  }

  discardChanges() {
    this.property = clone(this.propertyMemento);
    this.changesToSave.set({});
    this.reloadFields();
  }

  reloadFields() {
    this.fieldsComponents().forEach((field) => {
      field.loadComponent();
      this._changeDetectorRef.detectChanges();
    });
  }

  saveChanges() {
    this.savingChanges.set(true);

    this._propertiesService
      .saveProperty(this.changesToSave(), this.property)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.changesToSave.set({});
        this.savingChanges.set(false);
      });
  }

  lookupField(name) {
    const fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  chunk(array, size): any[] {
    const chunkedArr = [];
    const copied = [...array];
    const numOfChild = Math.ceil(copied.length / size); // Round up to the nearest integer
    for (let i = 0; i < numOfChild; i++) {
      chunkedArr.push(copied.splice(0, size));
    }
    return chunkedArr;
  }

  allowAddNewColumn(event, rowIndex) {
    this.rowActionDisabled[rowIndex] = event;
  }
}
